import { ReactNode, useMemo, useState, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Home, ClipboardList, BookOpen, Trophy, Bot, ArrowLeft } from 'lucide-react'
import { AnimatePresence } from 'motion/react'
import { PavelChatSheet } from './modals/PavelChatSheet'
import { useAuth } from '../hooks/useAuth'
import { useCurator } from '../contexts/CuratorContext'
import type { ProductFeatureMap } from '@tfa/shared'
import { completedBannerText } from '../utils/postProgram'
import { api } from '../services/api'
import { useTelegram } from '../hooks/useTelegram'
import { TabBar, type TabBarTab } from './TabBar'
import { LockedFeature } from './LockedFeature'
import { routeFeature, tabsForAccess } from '../utils/featureLocks'
import { featureState, type ProductFeature } from '@tfa/shared'
import { FreezeStatusChip } from './FreezeStatusChip'
import { ProgramPauseNotice } from './ProgramPauseNotice'
import { ProgramRenewal } from './ProgramRenewal'
import { LevelUpModal } from './modals/LevelUpModal'
import { TUCOIN_LEVEL_BONUSES } from '@tfa/shared'
import type { CharacterGender } from '../assets/character'

const DEMO_STATUSES = new Set(['AWAITING_QUESTIONNAIRE', 'AWAITING_CURATOR', 'AWAITING_START'])

// #349: на этапе подбора куратора и ожидания старта разрешаем «Обучение».
// Лекции нулевого модуля / первой недели бэкенд уже отдаёт открытыми — их
// закрывал только демо-оверлей. AWAITING_QUESTIONNAIRE сюда НЕ входит: там
// анкета ещё не сдана, лекции преждевременны.
const LECTURE_UNLOCK_STATUSES = new Set(['AWAITING_CURATOR', 'AWAITING_START'])

interface LayoutProps {
  children: ReactNode
  hideNav?: boolean
  hideHeader?: boolean
  title?: string
}

type TabFeature = ProductFeature | null

const ALL_TABS: (TabBarTab & { feature: TabFeature })[] = [
  { icon: Home,          label: 'Главная',  path: '/home',        accent: '#A29BFE', dot: 'rgba(162,155,254,0.9)', feature: null },
  { icon: BookOpen,      label: 'Контент',  path: '/content',     accent: '#55EFC4', dot: 'rgba(85,239,196,0.9)',  feature: 'lectures' },
  // Дневник — центральный таб и стартовый экран приложения
  { icon: ClipboardList, label: 'Дневник',  path: '/diary',       accent: '#74B9FF', dot: 'rgba(116,185,255,0.9)', feature: 'diary' },
  { icon: Trophy,        label: 'Рейтинг',  path: '/leaderboard', accent: '#FDCB6E', dot: 'rgba(253,203,110,0.9)', feature: 'leaderboard' },
  { icon: Bot,           label: 'ИИ Павел', path: '__pavel',      accent: '#A29BFE', dot: 'rgba(162,155,254,0.9)', feature: 'ai', forceInactive: true },
]

// Дата старта тест-недели (flowStartDate) → «20 июня, пятница».
const _WD = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота']
const _MON = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
export function formatStartDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return `${d.getUTCDate()} ${_MON[d.getUTCMonth()]}, ${_WD[d.getUTCDay()]}`
}

/** Дата окончания паузы → «14 августа». */
export function formatPauseUntil(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getUTCDate()} ${_MON[d.getUTCMonth()]}`
}

const STATUS_MESSAGES: Record<string, { text: string; actionable: boolean }> = {
  PROGRAM_PAUSE: { text: 'Программа на паузе', actionable: false },
  AWAITING_QUESTIONNAIRE: { text: 'Заполните анкету, чтобы начать', actionable: true },
  AWAITING_CURATOR: { text: 'Подбираем куратора…', actionable: false },
  AWAITING_START: { text: 'Куратор назначен — скоро старт', actionable: false },
  TEST_WEEK: { text: 'Тестовая неделя — знакомьтесь с приложением', actionable: false },
  COMPLETED: { text: 'Программа завершена — режим просмотра', actionable: false },
}

const OVERLAY_DISMISS_KEY_PREFIX = 'tfa.demoOverlayDismissed.'

function readDismissed(status: string | undefined): boolean {
  if (!status || typeof window === 'undefined') return false
  try {
    return window.sessionStorage.getItem(OVERLAY_DISMISS_KEY_PREFIX + status) === '1'
  } catch {
    return false
  }
}

function writeDismissed(status: string | undefined, value: boolean): void {
  if (!status || typeof window === 'undefined') return
  try {
    const key = OVERLAY_DISMISS_KEY_PREFIX + status
    if (value) window.sessionStorage.setItem(key, '1')
    else window.sessionStorage.removeItem(key)
  } catch {
    /* sessionStorage unavailable — fall back to in-memory state only */
  }
}

export function Layout({ children, hideNav, hideHeader }: LayoutProps) {
  const { user } = useAuth()
  const { isCurator, viewingClientId, viewingClientName, isAdminView, exitClient } = useCurator()
  const navigate = useNavigate()
  const location = useLocation()
  const { openLink } = useTelegram()
  const status = (user as any)?.status as string | undefined
  // Клиенту нужен куратор, а его нет — например, прежний уволился, пока человек
  // был на паузе. Показываем плашку «Подбираем куратора…», но НЕ подменяем
  // status: AWAITING_CURATOR входит в DEMO_STATUSES и закрыл бы вкладки, а у
  // вернувшегося клиента программа идёт — дневник и тренировки ему нужны.
  const awaitingCurator = !!(user as any)?.awaitingCuratorAssignment && status === 'IN_PROGRESS'
  // Пауза программы (куплена клиентом в магазине). Бэк отдаёт дату только пока
  // пауза идёт, но клиент мог не перезагружать мини-апп сутками — поэтому
  // сверяемся с текущим временем ещё и здесь.
  const rawPauseUntil = (user as any)?.programPauseUntil as string | null | undefined
  const programPauseUntil = rawPauseUntil && new Date(rawPauseUntil) > new Date() ? rawPauseUntil : null
  const mode = (user as any)?.mode as string | undefined
  // Persist dismissal across navigations: each route re-mounts Layout, so a
  // plain useState would re-show the overlay on every screen change.
  const [overlayDismissed, setOverlayDismissedState] = useState<boolean>(() => readDismissed(status))
  const [overlayShaking, setOverlayShaking] = useState(false)

  useEffect(() => {
    setOverlayDismissedState(readDismissed(status))
  }, [status])

  const setOverlayDismissed = useCallback((value: boolean) => {
    setOverlayDismissedState(value)
    writeDismissed(status, value)
  }, [status])

  const [showPavelSheet, setShowPavelSheet] = useState(false)
  // Три тарифа: ИИ-Павел под замком открывается не чатом, а полноэкранным замком.
  const [showLockedAi, setShowLockedAi] = useState(false)
  const access = (user as { access?: Partial<ProductFeatureMap> | null } | undefined)?.access
  const product = user?.product ?? null

  const tabs = useMemo(() => {
    // Три тарифа (этап 2): открытые вкладки — как есть, под замком — видны с
    // замком, выключенные — скрыты. Карта тарифа приходит с сервера (user.access),
    // дефолты из @tfa/shared — запасной путь.
    return tabsForAccess(ALL_TABS, product, access).map(t => t.path === '__pavel'
      ? { ...t, onClick: () => (t.locked ? setShowLockedAi(true) : setShowPavelSheet(true)) }
      : t,
    )
  }, [product, access])

  // Маршрут раздела под замком: страница рендерится под размытием с плашкой.
  const lockedRouteFeature = useMemo(() => {
    // Куратор в режиме просмотра — не клиент, его тариф тут ни при чём. Админский
    // impersonate — «увидеть как клиент», замок показываем.
    if (isCurator || viewingClientId) return null
    const f = routeFeature(location.pathname)
    return f && featureState(product, f, access) === 'locked' ? f : null
  }, [location.pathname, product, access, isCurator, viewingClientId])

  // Badge: check if diary has incomplete daily actions
  const { data: dailyStatus } = useQuery({
    queryKey: ['actions', 'daily-status'],
    queryFn: () => api.getDailyStatus(),
    staleTime: 60_000,
    enabled: !!user && !viewingClientId,
  })

  const diaryBadge = useMemo(() => {
    if (!dailyStatus) return false
    // Show badge if any core daily action is not done
    return !dailyStatus.nutrition || !dailyStatus.sleep || !dailyStatus.steps
  }, [dailyStatus])

  // Badge: unread curator comments (across all report types)
  const { data: unreadData } = useQuery({
    queryKey: ['diary', 'unread-count'],
    queryFn: () => api.getUnreadCommentsCount(),
    staleTime: 20_000,
    refetchInterval: 60_000,
    enabled: !!user && !viewingClientId,
  })
  const unreadComments = unreadData?.total ?? 0

  // #292: поп-ап нового уровня. Основной путь — SSE `level.up` (useServerEvents
  // диспатчит window-событие). Догоняющий путь — сравнение user.level с
  // последним увиденным (localStorage): ловит левел-апы, случившиеся при
  // закрытом мини-аппе. Не показываем куратору/при импersonации.
  const [levelUp, setLevelUp] = useState<{ open: boolean; from: number; to: number; coins: number }>({ open: false, from: 1, to: 2, coins: 0 })
  const userId = (user as any)?.id as string | undefined
  const userLevel = (user as any)?.level as number | undefined
  const userGender = (user as any)?.gender as CharacterGender
  const levelSeenKey = userId ? `tfa-level-seen:${userId}` : null
  const markLevelSeen = useCallback((lvl: number) => {
    if (!levelSeenKey) return
    try { localStorage.setItem(levelSeenKey, String(lvl)) } catch { /* ignore */ }
  }, [levelSeenKey])
  useEffect(() => {
    if (isCurator || viewingClientId) return
    const onLevelUp = (e: Event) => {
      const d = (e as CustomEvent).detail as { level: number; previousLevel?: number; coins?: number }
      if (typeof d?.level !== 'number') return
      setLevelUp({ open: true, from: d.previousLevel ?? Math.max(1, d.level - 1), to: d.level, coins: d.coins ?? 0 })
      markLevelSeen(d.level)
    }
    window.addEventListener('tfa:level-up', onLevelUp)
    return () => window.removeEventListener('tfa:level-up', onLevelUp)
  }, [isCurator, viewingClientId, markLevelSeen])
  useEffect(() => {
    if (isCurator || viewingClientId || !levelSeenKey || typeof userLevel !== 'number') return
    let seen: number | null = null
    try {
      const raw = localStorage.getItem(levelSeenKey)
      seen = raw != null ? parseInt(raw, 10) : null
    } catch { /* ignore */ }
    if (seen == null || Number.isNaN(seen)) {
      // Первый запуск с этой фичей — молча запоминаем текущий уровень.
      markLevelSeen(userLevel)
      return
    }
    if (userLevel > seen) {
      let coins = 0
      for (let lvl = seen + 1; lvl <= userLevel; lvl++) coins += TUCOIN_LEVEL_BONUSES[lvl] || 0
      setLevelUp({ open: true, from: seen, to: userLevel, coins })
      markLevelSeen(userLevel)
    } else if (userLevel < seen) {
      // Уровень откатили (сброс/админ) — не показываем поп-ап, просто догоняем.
      markLevelSeen(userLevel)
    }
  }, [userLevel, levelSeenKey, isCurator, viewingClientId, markLevelSeen])

  // Refresh unread-comments counter when the client navigates to a page that
  // marks comments as read server-side (delayed to let the page's own fetch finish).
  const queryClient = useQueryClient()
  useEffect(() => {
    const paths = ['/diary', '/measurements', '/progress-photos']
    if (!paths.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'))) return
    const t = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['diary', 'unread-count'] })
      queryClient.invalidateQueries({ queryKey: ['diary', 'unread-by-date'] })
    }, 1000)
    return () => clearTimeout(t)
  }, [location.pathname, queryClient])

  const isDemoState = !!(status && DEMO_STATUSES.has(status))
  // #349: страницы «Обучение» (/content, /lectures) при подборе куратора /
  // ожидании старта оставляем полностью интерактивными — не поднимаем оверлей
  // и не гасим содержимое.
  const onLecturesPath = location.pathname.startsWith('/content') || location.pathname.startsWith('/lectures')
  const lecturesUnlocked = onLecturesPath && !!status && LECTURE_UNLOCK_STATUSES.has(status)
  const showFrostOverlay = isDemoState && !overlayDismissed && !lecturesUnlocked
  const showPeekBanner = isDemoState && overlayDismissed
  // Home page handles its own demo dimming inline. For the other tabs (Diary,
  // Lectures, Leaderboard, Profile, Shop, etc.) wrap content in a dim layer so
  // widgets look identically frozen across the app.
  // В демо-режиме (AWAITING_*) затемняем содержимое НЕ-главных экранов, чтобы
  // не сбивать пользователя. Главные экраны (Главная и Дневник) оставляем
  // полноценно интерактивными: на Главной — карточка персонажа + FatSecret CTA,
  // на Дневнике — FatSecret подключение в блоке Питание. Профиль/Настройки
  // тоже всегда доступны (для тестовых сценариев).
  const BRIGHT_DEMO_PATHS = ['/', '/home', '/diary', '/profile']
  const dimNonHomeContent = isDemoState && !BRIGHT_DEMO_PATHS.includes(location.pathname) && !lecturesUnlocked
  // Top banner key: TEST_WEEK while not completed, or COMPLETED.
  const topBannerKey = status === 'COMPLETED'
    ? 'COMPLETED'
    : programPauseUntil
      ? 'PROGRAM_PAUSE'
      : awaitingCurator
        ? 'AWAITING_CURATOR'
        : (mode === 'TEST_WEEK' ? 'TEST_WEEK' : null)
  const statusMessage = status ? STATUS_MESSAGES[status] : null
  const topBannerMessage = topBannerKey ? STATUS_MESSAGES[topBannerKey] : null
  // Паузу подписываем сроком и последствием: без этого человек не понимает,
  // сколько ещё продлится и почему приложение ведёт себя тихо.
  const topBannerText = topBannerKey === 'PROGRAM_PAUSE' && programPauseUntil
    ? `❄️ Программа на паузе до ${formatPauseUntil(programPauseUntil)} — жизни не снимаются, серия сохраняется`
    : topBannerKey === 'COMPLETED'
      // После завершения клиенту ещё на срок остаются лекции и тренировки —
      // баннер называет, что доступно и до какого числа
      ? completedBannerText((user as any)?.completedAt, (user as any)?.product)
      : topBannerMessage?.text

  // Shake handler for frost overlay (doc 17)
  const handleOverlayTap = useCallback(() => {
    if (overlayShaking) return
    setOverlayShaking(true)
    setTimeout(() => setOverlayShaking(false), 300)
  }, [overlayShaking])

  // Note: peek-mode tooltip "Ожидай старт" is handled inside Home via a
  // local click catcher scoped to the dimmed wrapper, so it doesn't block
  // the FatSecret connect card (the only bright CTA in demo mode).

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        margin: '0 auto',
        minHeight: '100vh',
        background: isCurator ? '#FAF3E0' : '#F0F4F8',
        position: 'relative',
        fontFamily: 'Roboto, sans-serif',
        overflowX: 'hidden',
      }}
    >
      {/* Curator viewing mode header */}
      {viewingClientId && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 16px',
          background: 'linear-gradient(135deg, #6C5CE7, #A29BFE)',
          color: 'white',
          fontSize: 14,
          fontWeight: 600,
          position: 'sticky',
          top: 0,
          zIndex: 150,
        }}>
          <button
            onClick={() => {
              exitClient()
              navigate(-1)
            }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 10,
              background: 'rgba(255,255,255,0.2)', border: 'none',
              cursor: 'pointer',
            }}
          >
            <ArrowLeft size={18} color="white" />
          </button>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {viewingClientName || 'Клиент'}
          </span>
          <span style={{
            fontSize: 10, fontWeight: 600,
            background: 'rgba(255,255,255,0.2)', borderRadius: 6, padding: '3px 8px',
          }}>
            {isAdminView ? 'Админ · только чтение' : 'Куратор'}
          </span>
        </div>
      )}

      {/* Top informational banner — only TEST_WEEK / COMPLETED.
          Demo statuses use center overlay (initial) → bottom sticky banner (peek). */}
      {topBannerMessage && !hideHeader && !isDemoState && (
        <div data-testid={
          topBannerKey === 'TEST_WEEK' ? 'test-week-banner'
            : topBannerKey === 'PROGRAM_PAUSE' ? 'program-pause-banner'
              : 'completed-banner'
        } style={{
          padding: '8px 16px',
          // Пауза — не тревога, а спокойное состояние: холодный синий вместо
          // общего серо-синего, чтобы её было видно среди прочих плашек.
          background: topBannerKey === 'PROGRAM_PAUSE' ? 'rgba(116,185,255,0.18)' : 'rgba(26,58,92,0.08)',
          fontSize: 13,
          color: '#1A3A5C',
          textAlign: 'center',
          fontWeight: 500,
        }}>
          {topBannerText}
        </div>
      )}

      {/* #602: «Продлить» программу — за неделю до конца доступа и после него. */}
      {!isCurator && !viewingClientId && !hideHeader && (
        <ProgramRenewal
          accessEnd={(user as any)?.subscriptionEnd}
          links={(user as any)?.programRenewal}
        />
      )}


      {/* Content */}
      <div
        style={{
          paddingTop: 0,
          paddingBottom: hideNav ? 0 : 92,
          ...(dimNonHomeContent
            ? { opacity: 0.35, filter: 'grayscale(0.4)', pointerEvents: 'none' as const, transition: 'opacity 0.3s ease' }
            : {}),
        }}
      >
        {lockedRouteFeature ? <LockedFeature feature={lockedRouteFeature}>{children}</LockedFeature> : children}
      </div>

      {showLockedAi && <LockedFeature feature="ai" onClose={() => setShowLockedAi(false)} />}

      {!hideNav && (
        <TabBar tabs={tabs} unreadComments={unreadComments} diaryBadge={diaryBadge} />
      )}

      <AnimatePresence>
        {showPavelSheet && (
          <PavelChatSheet onClose={() => setShowPavelSheet(false)} />
        )}
      </AnimatePresence>

      {/* Глобальный индикатор активной заморозки — виден на всех экранах,
          где есть Layout. Сам решает, когда показать модалку (1 раз/сессия). */}
      {user && !viewingClientId && !isCurator && <FreezeStatusChip />}

      {/* #444: пауза программы. Узкую плашку вверху люди не замечали и писали
          в поддержку «заморозка вообще включилась?» — поэтому при входе
          карточка с датой окончания, дальше постоянный чип. */}
      {user && !viewingClientId && !isCurator && (
        <ProgramPauseNotice pauseUntil={programPauseUntil} />
      )}

      {/* #292: поп-ап повышения уровня — глобальный, на всех клиентских экранах */}
      {user && !viewingClientId && !isCurator && (
        <LevelUpModal
          open={levelUp.open}
          onClose={() => setLevelUp((s) => ({ ...s, open: false }))}
          fromLevel={levelUp.from}
          toLevel={levelUp.to}
          coins={levelUp.coins}
          gender={userGender}
          // #355: если внешность персонажа поднята админом, поп-ап повышения
          // тоже показывает поднятую — иначе на радостном экране клиент увидит
          // ровно того маскота, от которого его избавили.
          minCharacterLevel={(user as { characterMinLevel?: number | null })?.characterMinLevel ?? null}
        />
      )}

      {/* Bottom sticky demo banner (peek mode) — prototype DemoOverlay style */}
      {showPeekBanner && statusMessage && (
        <div
          data-testid="demo-peek-banner"
          data-flow-state={status}
          style={{
            position: 'fixed',
            left: '50%',
            bottom: `calc(${hideNav ? 16 : 84}px + env(safe-area-inset-bottom))`,
            transform: 'translateX(-50%)',
            zIndex: 180,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            height: 40,
            padding: '0 16px',
            borderRadius: 20,
            background: 'rgba(22,20,60,0.88)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.14)',
            boxShadow: '0 6px 24px rgba(0,0,0,0.28)',
            color: 'rgba(255,255,255,0.92)',
            fontSize: 12,
            fontWeight: 600,
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {status === 'AWAITING_QUESTIONNAIRE' && '📋'}
            {status === 'AWAITING_CURATOR' && '⏳'}
            {status === 'AWAITING_START' && '📅'}
            {' '}{statusMessage.text}
          </span>
          <button
            onClick={() => setOverlayDismissed(false)}
            style={{
              marginLeft: 4,
              height: 24,
              padding: '0 10px',
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.22)',
              background: 'rgba(255,255,255,0.08)',
              color: 'white',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Открыть
          </button>
        </div>
      )}

      {/* Frost overlay for demo mode (doc 17) */}
      {showFrostOverlay && (
        <div
          data-testid="demo-frost-overlay"
          data-flow-state={status}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.6)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 16,
            padding: 32,
            textAlign: 'center',
            animation: overlayShaking ? 'frost-shake 0.3s ease' : undefined,
          }}
          onClick={(e) => {
            e.stopPropagation()
            handleOverlayTap()
          }}
        >
          <h2 data-testid="demo-overlay-title" style={{ color: '#1A3A5C', fontSize: 20, fontWeight: 600 }}>{statusMessage?.text}</h2>

          {status === 'AWAITING_QUESTIONNAIRE' && (
            <>
              <p style={{ color: '#6B7B8D', fontSize: 15 }}>Чтобы начать, заполни анкету</p>
              <button
                data-testid="demo-overlay-cta-primary"
                onClick={() => navigate('/onboarding')}
                style={{ width: '100%', maxWidth: 280, height: 52, borderRadius: 16, border: 'none', background: 'linear-gradient(135deg, #1A3A5C, #4A6CF7)', color: 'white', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}
              >
                Заполнить
              </button>
            </>
          )}

          {status === 'AWAITING_CURATOR' && (
            <>
              {/* Timer illustration */}
              <div style={{ marginBottom: 4 }}>
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="40" cy="40" r="36" fill="rgba(74,108,247,0.08)" stroke="rgba(74,108,247,0.18)" strokeWidth="2"/>
                  {/* Clock face */}
                  <circle cx="40" cy="40" r="22" fill="white" stroke="#4A6CF7" strokeWidth="2"/>
                  {/* Hour markers */}
                  <line x1="40" y1="21" x2="40" y2="25" stroke="#4A6CF7" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="40" y1="55" x2="40" y2="59" stroke="#4A6CF7" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="21" y1="40" x2="25" y2="40" stroke="#4A6CF7" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="55" y1="40" x2="59" y2="40" stroke="#4A6CF7" strokeWidth="2" strokeLinecap="round"/>
                  {/* Minute hand (pointing ~10 min) */}
                  <line x1="40" y1="40" x2="40" y2="26" stroke="#1A3A5C" strokeWidth="2.5" strokeLinecap="round">
                    <animateTransform attributeName="transform" type="rotate" from="0 40 40" to="360 40 40" dur="60s" repeatCount="indefinite"/>
                  </line>
                  {/* Hour hand (pointing ~3 o'clock area) */}
                  <line x1="40" y1="40" x2="51" y2="40" stroke="#4A6CF7" strokeWidth="2.5" strokeLinecap="round">
                    <animateTransform attributeName="transform" type="rotate" from="0 40 40" to="360 40 40" dur="720s" repeatCount="indefinite"/>
                  </line>
                  <circle cx="40" cy="40" r="2.5" fill="#4A6CF7"/>
                  {/* Decorative dots around */}
                  <circle cx="40" cy="8" r="3" fill="#4A6CF7" opacity="0.3"/>
                  <circle cx="68" cy="28" r="2" fill="#74B9FF" opacity="0.4"/>
                  <circle cx="14" cy="58" r="2.5" fill="#A29BFE" opacity="0.35"/>
                </svg>
              </div>
              <p style={{ color: '#6B7B8D', fontSize: 14, lineHeight: 1.5, maxWidth: 260 }}>
                Процесс может занимать до 3 рабочих дней, не переживай. Если возникли вопросы — обращайся в отдел заботы.
              </p>
              <button
                data-testid="demo-overlay-cta-support"
                onClick={() => openLink('https://t.me/sup_TFA')}
                style={{ width: '100%', maxWidth: 280, height: 48, borderRadius: 14, border: 'none', background: 'linear-gradient(135deg, #1A3A5C, #4A6CF7)', color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21.8 2.15L1.4 9.9c-1.3.5-1.3 1.3-.2 1.6l5.2 1.6 12-7.6c.6-.4 1.1-.2.7.2L7.6 16.4v2l3.8-3.4 7.5 5.5c1.4.8 2.4.4 2.7-1.3l2.4-15.5c.4-1.6-.6-2.3-2.2-1.55z" fill="white"/></svg>
                Написать в поддержку
              </button>
              <button
                data-testid="demo-overlay-cta-dismiss"
                onClick={() => setOverlayDismissed(true)}
                style={{ width: '100%', maxWidth: 280, height: 44, borderRadius: 12, border: 'none', background: 'rgba(74,159,229,0.12)', color: '#4A9FE5', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
              >
                ОК
              </button>
            </>
          )}

          {status === 'AWAITING_START' && (
            <>
              <div style={{ fontSize: 44 }}>🎉</div>
              {(() => {
                const label = formatStartDate((user as { flowStartDate?: string } | undefined)?.flowStartDate)
                return (
                  <>
                    <p style={{ color: '#1A3A5C', fontSize: 16, fontWeight: 600, margin: 0 }}>
                      Куратор назначен!
                    </p>
                    <p style={{ color: '#6B7B8D', fontSize: 14.5, lineHeight: 1.55, maxWidth: 280, margin: 0 }}>
                      {label
                        ? <>Тестовая неделя стартует <b style={{ color: '#4A6CF7' }}>{label}</b>. До старта можно знакомиться с приложением — позже придёт уведомление.</>
                        : <>Тестовая неделя скоро стартует. До старта можно знакомиться с приложением — придёт уведомление.</>}
                    </p>
                  </>
                )
              })()}
              <button
                data-testid="demo-overlay-cta-dismiss"
                onClick={() => setOverlayDismissed(true)}
                style={{ width: '100%', maxWidth: 280, height: 48, borderRadius: 14, border: 'none', background: 'linear-gradient(135deg, #1A3A5C, #4A6CF7)', color: 'white', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}
              >
                ОК
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
